import { Box, Divider, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SettingItem } from "../../hooks/useSettingSearch";

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(re);
  return (
    <>
      {parts.map((part, i) =>
        re.test(part) ? (
          <Box key={i} component="mark" sx={{ bgcolor: "#FFF176", color: "inherit", borderRadius: "2px", px: "1px" }}>
            {part}
          </Box>
        ) : (
          part
        ),
      )}
    </>
  );
}

interface Props {
  query: string;
  results: SettingItem[];
  onSearch: (q: string) => void;
  onNavigate: (item: SettingItem) => void;
}

export default function SettingSearchBar({ query, results, onSearch, onNavigate }: Props) {
  const showDropdown = query.trim().length > 0;

  return (
    <Box sx={{ flex: 1, position: "relative" }}>
      <TextField
        size="small"
        placeholder="설정 검색..."
        value={query}
        onChange={(e) => onSearch(e.target.value)}
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end" sx={{ cursor: "pointer" }} onClick={() => onSearch("")}>
              <ClearIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            </InputAdornment>
          ) : null,
        }}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            fontSize: 13,
            borderRadius: 2,
            bgcolor: "grey.50",
            "& fieldset": { borderColor: "grey.300" },
            "&:hover fieldset": { borderColor: "grey.400" },
            "&.Mui-focused fieldset": { borderColor: "primary.main", borderWidth: 1 },
            "&.Mui-focused": { bgcolor: "background.paper" },
          },
        }}
      />

      {/* 드롭다운 */}
      {showDropdown && (
        <Box
          sx={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            bgcolor: "background.paper",
            border: "0.5px solid",
            borderColor: "grey.200",
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
            overflow: "hidden",
          }}
        >
          {/* 헤더 */}
          <Box sx={{ px: 1.5, py: 0.75, bgcolor: "grey.50", borderBottom: "0.5px solid", borderColor: "grey.200" }}>
            <Typography fontSize={11} color="text.secondary">
              {results.length > 0 ? `검색 결과 ${results.length}개` : "결과 없음"}
            </Typography>
          </Box>

          {results.length === 0 ? (
            <Box sx={{ px: 2, py: 2.5, textAlign: "center" }}>
              <Typography fontSize={13} color="text.secondary">
                "{query}"에 해당하는 설정이 없습니다
              </Typography>
            </Box>
          ) : (
            results.map((item, i) => (
              <Box key={item.id}>
                {i > 0 && results[i - 1].sectionId !== item.sectionId && <Divider sx={{ borderColor: "grey.100" }} />}
                <Box
                  onClick={() => onNavigate(item)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    gap: 1,
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize={10} color="text.secondary" mb={0.25}>
                      <Highlight text={item.sectionTitle} query={query} />
                    </Typography>
                    <Typography fontSize={13} fontWeight={500}>
                      <Highlight text={item.label} query={query} />
                    </Typography>
                    {item.description && (
                      <Typography fontSize={11} color="text.secondary" noWrap>
                        <Highlight text={item.description} query={query} />
                      </Typography>
                    )}
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}
